import { PageTabsContext } from '@components/Common/PageHeader';
import JobApplicants from '@components/JobApplicants';
import PipelineBoard from '@components/PipelineBoard';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import './index.scss';

const TAB_SLUG: Record<number, string> = { 0: 'list', 1: 'board' };
const SLUG_TAB: Record<string, number> = { list: 0, board: 1 };

/**
 * Applications — unified view over the same getAllJobApplications data:
 *   List  = the full applications table (JobApplicants)
 *   Board = the managed-hiring Kanban (PipelineBoard)
 *
 * Both tabs are open to HR as well as Super Admin: working a pipeline IS the dedicated HR's job.
 * There is nothing to hide at the tab level because the server scopes the data itself — an HR
 * sees every regular (self-serve) client plus the premium clients assigned to them, and never
 * another HR's premium client (see JobApplicationsService). Both tabs open the same detail route
 * /job-applications/:id, which re-checks that same scope.
 */
const Applications: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const activeTab = SLUG_TAB[tab ?? 'list'] ?? 0;

  const handleTabChange = (newValue: number) => {
    navigate(`/applications/${TAB_SLUG[newValue]}`, { replace: true });
  };

  const tabs = [
    { label: 'List', icon: <FormatListBulletedOutlinedIcon fontSize="small" /> },
    { label: 'Board', icon: <ViewKanbanOutlinedIcon fontSize="small" /> },
  ];

  return (
    // The tabs travel down to each view's own CRMPageTemplate, which draws them directly under
    // its header: the H1 first, then the switch. They used to sit above the H1.
    <PageTabsContext.Provider value={{ value: activeTab, onChange: handleTabChange, tabs }}>
      <Box className="apps-root">
        {/* Render-and-hide to preserve each view's state on tab switch */}
        <Box hidden={activeTab !== 0} className="apps-panel"><JobApplicants /></Box>
        <Box hidden={activeTab !== 1} className="apps-panel"><PipelineBoard /></Box>
      </Box>
    </PageTabsContext.Provider>
  );
};

export default Applications;
